import React, { useEffect, useCallback } from 'react';
import { Eye, Edit, MoreVertical, Download, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import UserDetailModal from './UserDetailModal';
import useUsersStore from '@/stores/AdminStore/UsersStore';
import { LoadingSpinner } from '@/Components/common';
import './UserDirectoryTab.css';

const UserDirectoryTab: React.FC = () => {
  const {
    users,
    departments,
    usersPagination,
    selectedUser,
    searchTerm,
    selectedDepartment,
    selectedStatus,
    currentPage,
    itemsPerPage,
    loadingUsers,
    fetchUsers,
    fetchUserById,
    setSearchTerm,
    setSelectedDepartment,
    setSelectedStatus,
    setCurrentPage,
    setItemsPerPage,
    setSelectedUser,
  } = useUsersStore();

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers({ page: 1 });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, selectedDepartment, selectedStatus]);

  // Fetch on pagination change
  useEffect(() => {
    fetchUsers({ page: currentPage, limit: itemsPerPage });
  }, [currentPage, itemsPerPage]);

  const handleViewUser = useCallback(async (userId: string) => {
    const user = await fetchUserById(userId);
    if (!user) {
      toast.error('Failed to load user details');
    }
  }, [fetchUserById]);

  const handleCloseModal = useCallback(() => {
    setSelectedUser(null);
  }, [setSelectedUser]);

  const handleEnrollCourse = (courseName: string) => {
    toast.info(`Enrolling user in: ${courseName}`);
  };

  const handleExport = () => {
    toast.info('Export functionality coming soon');
  };

  const totalPages = usersPagination?.totalPages || 1;
  const totalCount = usersPagination?.total || 0;

  // Format last login date
  const formatLastLogin = (dateString: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffHours < 48) return 'Yesterday';
    return date.toLocaleDateString();
  };

  return (
    <div className="admin-user-directory">
      <div className="admin-user-directory-controls">
        <div className="admin-user-directory-search">
          <Search className="admin-user-directory-search-icon" size={18} />
          <input
            type="text"
            placeholder="Search users by name, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          className="admin-user-directory-select"
          value={selectedDepartment}
          onChange={(e) => {
            setSelectedDepartment(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option>All Department</option>
          {departments.map((dept) => (
            <option key={dept} value={dept}>
              {dept}
            </option>
          ))}
        </select>
        <select
          className="admin-user-directory-select"
          value={selectedStatus}
          onChange={(e) => {
            setSelectedStatus(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option>All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <button className="admin-user-directory-export-btn" onClick={handleExport}>
          <Download size={18} />
          Export
        </button>
      </div>

      <div className="admin-user-directory-table-container">
        {loadingUsers ? (
          <div className="admin-user-directory-loading">
            <LoadingSpinner size="md" />
          </div>
        ) : users.length === 0 ? (
          <div className="admin-user-directory-empty">
            <p>No users found</p>
          </div>
        ) : (
          <table className="admin-user-directory-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Department</th>
                <th>Job Title</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Course</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="admin-user-directory-user-cell">
                      <div className="admin-user-directory-avatar">{user.initials}</div>
                      <div className="admin-user-directory-user-info">
                        <span className="admin-user-directory-user-name">{user.name}</span>
                        <span className="admin-user-directory-user-email">{user.email}</span>
                      </div>
                    </div>
                  </td>
                  <td>{user.department}</td>
                  <td>{user.jobTitle}</td>
                  <td>
                    <span className={`admin-user-directory-status-badge ${user.status.toLowerCase()}`}>
                      {user.status}
                    </span>
                  </td>
                  <td>{formatLastLogin(user.lastLogin)}</td>
                  <td>
                    <div className="admin-user-directory-course-info">
                      <span className="admin-user-directory-course-completed">{user.coursesCompleted} completed</span>
                      <span className="admin-user-directory-course-active">{user.coursesActive} active</span>
                    </div>
                  </td>
                  <td>
                    <div className="admin-user-directory-actions">
                      <button
                        className="admin-user-directory-action-btn"
                        onClick={() => handleViewUser(user.id)}
                        title="View details"
                      >
                        <Eye size={18} />
                      </button>
                      <button className="admin-user-directory-action-btn" title="Edit">
                        <Edit size={18} />
                      </button>
                      <button className="admin-user-directory-action-btn" title="More options">
                        <MoreVertical size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="admin-user-directory-pagination">
          <div className="admin-user-directory-pagination-info">
            <span>Showing</span>
            <select
              className="admin-user-directory-pagination-select"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span>of {totalCount}</span>
          </div>

          <div className="admin-user-directory-pagination-buttons">
            <button
              className="admin-user-directory-page-btn"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              ‹
            </button>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let pageNum = i + 1;
              // Adjust page numbers if we're past page 3
              if (currentPage > 3 && totalPages > 5) {
                pageNum = currentPage - 2 + i;
                if (pageNum > totalPages) pageNum = totalPages - (4 - i);
              }
              if (pageNum < 1 || pageNum > totalPages) return null;
              return (
                <button
                  key={pageNum}
                  className={`admin-user-directory-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              className="admin-user-directory-page-btn"
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
            >
              ›
            </button>
          </div>
        </div>
      </div>

      {selectedUser && (
        <UserDetailModal
          user={{
            id: selectedUser.id,
            name: selectedUser.name,
            email: selectedUser.email || '',
            jobTitle: selectedUser.jobTitle,
            department: selectedUser.department,
            initials: selectedUser.initials,
            progress: 0,
            phone: selectedUser.phone,
            location: selectedUser.location,
            manager: selectedUser.manager,
            joinDate: selectedUser.joinDate || '',
            lastLogin: selectedUser.lastLogin,
            completedCourses: selectedUser.coursesCompleted,
            inProgressCourses: selectedUser.coursesActive,
          }}
          courses={[]}
          learningHistory={[]}
          adminActivities={[]}
          onClose={handleCloseModal}
          onEnrollCourse={handleEnrollCourse}
        />
      )}
    </div>
  );
};

export default UserDirectoryTab;
